/**
 * Registration of the real export generators (brief Section 12,
 * Phase 1 Block 5).
 *
 * The generators live in @sae/exports and consume a structural
 * ExportInput; this module is the adapter from the app's
 * ProjectState to that input, and it registers one registry entry
 * per export kind. The @sae/exports module is imported lazily inside
 * each generator so the export libraries (ExcelJS, docx, fflate,
 * statfmt) load as a separate chunk only when an export is actually
 * requested.
 *
 * The microdata and area-level run records are rebuilt with the
 * same pure pipeline functions the worker ran (prepareMicrodataRun,
 * tableToAreaLevelCell), so the exports describe the run that
 * actually happened rather than a recollection of it. Gating is
 * unchanged: the store's requestExport refuses while the license is
 * inactive, before any generator runs (decision S3).
 */

import type {
  AreaLevelRunRecord,
  ExportArtifact,
  ExportInput,
  ExportLabels,
  MicrodataRunRecord,
} from '@sae/exports';
import type { CollapseRule, IndicatorRule } from '@sae/automation';
import { normalizeAreaId, tableToAreaLevelCell } from '@sae/io';
import type { AreaLevelRoles, DataTable } from '@sae/io';

import { resolveTransform } from '../domain/defaults.js';
import { pathForKind } from '../domain/store.js';
import type { ProjectState, SetupState } from '../domain/types.js';
import { prepareMicrodataRun } from '../pipeline/buildInputs.js';
import type { ExportGenerator, ExportRegistry } from './registry.js';

function selectedRules(setup: SetupState): IndicatorRule[] {
  return setup.selectedIndicators
    .map((id) => setup.indicatorRules[id])
    .filter((r): r is IndicatorRule => r !== undefined);
}

function selectedCollapses(setup: SetupState): CollapseRule[] {
  return setup.subgroupsSelected
    .map((v) => setup.collapses[v])
    .filter((r): r is CollapseRule => r !== undefined);
}

function columnOf(table: DataTable, name: string | undefined) {
  if (name === undefined) return undefined;
  return table.columns.find((c) => c.name === name);
}

function areaNamesFor(state: ProjectState): Record<string, string> {
  const out: Record<string, string> = {};
  const table = state.source.table;
  const setup = state.setup;
  if (table === null || setup === null) return out;
  const { areaId, areaName } = setup.roles;
  if (areaId === undefined || areaName === undefined) return out;
  const areaIdx = table.columns.findIndex((c) => c.name === areaId);
  const nameIdx = table.columns.findIndex((c) => c.name === areaName);
  if (areaIdx < 0 || nameIdx < 0) return out;
  for (const row of table.rows) {
    const name = row[nameIdx];
    const area = row[areaIdx];
    if (name === null || name === undefined) continue;
    if (typeof area !== 'number' && typeof area !== 'string') continue;
    out[normalizeAreaId(area)] = String(name);
  }
  return out;
}

function labelsFor(state: ProjectState): ExportLabels {
  const indicators: Record<string, string> = {};
  const indicatorQuestions: Record<string, string> = {};
  const subgroupVariables: Record<string, string> = {};
  const subgroupLevels: Record<string, string> = {};
  const table = state.source.table;
  const setup = state.setup;
  if (setup !== null) {
    for (const rule of Object.values(setup.indicatorRules)) {
      const col = table !== null ? columnOf(table, rule.sourceColumn) : undefined;
      indicators[rule.id] = rule.label ?? col?.label ?? rule.id;
      if (col?.label != null) indicatorQuestions[rule.id] = col.label;
    }
    for (const variable of setup.subgroupsSelected) {
      const col = table !== null ? columnOf(table, variable) : undefined;
      subgroupVariables[variable] = col?.label ?? variable;
      if (col !== undefined) {
        for (const vl of col.valueLabels) {
          subgroupLevels[`${variable}|${String(vl.value)}`] = vl.label;
        }
      }
      const collapse = setup.collapses[variable];
      if (collapse !== undefined && collapse !== null) {
        for (const group of collapse.groups) {
          subgroupLevels[`${variable}|${String(group.key)}`] = group.label;
        }
      }
    }
  }
  return { indicators, indicatorQuestions, subgroupVariables, subgroupLevels };
}

function microdataRecordFor(state: ProjectState): MicrodataRunRecord | undefined {
  const setup = state.setup;
  const table = state.source.table;
  if (setup === null || table === null) return undefined;
  const rules = selectedRules(setup);
  const collapses = selectedCollapses(setup);
  const companion =
    state.covariatesSource !== null &&
    state.covariatesSource.table !== null &&
    state.covariatesSource.roles !== null
      ? {
          table: state.covariatesSource.table,
          roles: state.covariatesSource.roles,
        }
      : null;
  const prepared = prepareMicrodataRun({
    table,
    roles: setup.roles,
    indicatorRules: rules,
    collapses,
    subgroupsSelected: setup.subgroupsSelected,
    covariateModes: setup.covariateModes,
    companion,
    runConfig: state.config,
  });
  return {
    config: prepared.config,
    respondents: prepared.input.respondents,
    areaCovariates: prepared.input.areaCovariates ?? [],
    populationCounts: prepared.input.populationCounts ?? null,
    roles: setup.roles,
    indicatorRules: rules,
    collapses,
    subgroupsSelected: setup.subgroupsSelected,
  };
}

function areaLevelRecordFor(state: ProjectState): AreaLevelRunRecord | undefined {
  const setup = state.setup;
  const table = state.source.table;
  if (setup === null || table === null) return undefined;
  const roles = setup.roles;
  if (
    roles.areaId === undefined ||
    roles.estimate === undefined ||
    roles.sampleSize === undefined
  ) {
    return undefined;
  }
  const ioRoles: AreaLevelRoles = {
    area: roles.areaId,
    estimate: roles.estimate,
    ...(roles.variance !== undefined ? { variance: roles.variance } : {}),
    ...(roles.standardError !== undefined
      ? { standardError: roles.standardError }
      : {}),
    sampleSize: roles.sampleSize,
    ...(roles.populationCount !== undefined
      ? { populationCount: roles.populationCount }
      : {}),
    covariates: roles.covariates,
    indicatorId: setup.areaIndicatorId || 'indicator',
  };
  const conversion = tableToAreaLevelCell(table, ioRoles, {
    estimateScale: setup.estimateScale,
  });
  if (conversion.cell === null) return undefined;
  return {
    cell: conversion.cell,
    roles,
    estimateScale: setup.estimateScale,
    indicatorId: setup.areaIndicatorId || 'indicator',
  };
}

/** Adapt the app state to the exports package's input contract. */
export function exportInputFor(state: ProjectState): ExportInput {
  const result = state.run.result;
  if (result === null) {
    throw new Error('There are no results to export yet.');
  }
  const kind =
    state.setup !== null
      ? pathForKind(state.setup.assessment.classification.kind)
      : 'microdata';
  const cfg = state.config;
  return {
    projectName: state.projectName,
    licensee: state.license.email,
    result,
    settings: {
      mode: cfg.mode,
      transformChoice: cfg.transform,
      resolvedTransform: resolveTransform(cfg.transform),
      benchmarkingEnabled: cfg.benchmarkingEnabled,
      benchmarkingMethod: cfg.benchmarkingMethod,
      deffBasis: cfg.deffBasis === 'pooled' ? 'pooled' : 'statewide',
      transformBiasCorrection: cfg.transformBiasCorrection === true,
      disclosurePresetId: cfg.disclosurePresetId,
      bootstrapB: cfg.bootstrapB,
      seed: cfg.seed,
      confidence: cfg.confidence,
    },
    report: {
      surveyName: state.projectName,
      surveyYear: '',
      seriesTitle: 'Small Area Estimates',
      organization: '',
      dataSource: state.source.filename ?? 'the supplied data file',
    },
    labels: labelsFor(state),
    source: {
      kind: state.source.kind,
      filename: state.source.filename,
      format: state.source.format,
      table: state.source.table,
    },
    covariatesTable: state.covariatesSource?.table ?? null,
    areaNames: areaNamesFor(state),
    selectedCellId: state.selectedCellId,
    qc: state.qc,
    ...(kind === 'microdata'
      ? { microdata: microdataRecordFor(state) }
      : { areaLevel: areaLevelRecordFor(state) }),
  };
}

export function registerExportGenerators(registry: ExportRegistry): void {
  const wrap =
    (
      pick: (m: typeof import('@sae/exports')) => (
        input: ExportInput,
      ) => Promise<ExportArtifact>,
    ): ExportGenerator =>
    async ({ state }) => {
      const m = await import('@sae/exports');
      return pick(m)(exportInputFor(state));
    };
  registry.register('results-csv', wrap((m) => m.generateResultsCsv));
  registry.register('area-report-xlsx', wrap((m) => m.generateAreaReports));
  registry.register('master-dataset', wrap((m) => m.generateMasterDataset));
  registry.register('methods-report-docx', wrap((m) => m.generateMethodsReport));
  registry.register(
    'reproducibility-zip',
    wrap((m) => m.generateReproducibilityPackage),
  );
}
